import { useEffect, useState } from "react";
import { prs as prsApi } from "@/lib/api";
import { fmtAmt } from "@/lib/flags";
import { Sparkles, TrendingDown, AlertTriangle, Loader2, ArrowDownWideNarrow, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

export default function SavingsBanner({ approver, mode, onPrClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [allRows, setAllRows] = useState(null);
  const [allLoading, setAllLoading] = useState(false);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  const buildParams = (m, extra = {}) => {
    const params = { month: m, ...extra };
    if (approver && approver !== "__me__") {
      params.approver = approver === "all" ? "__all__" : approver;
    }
    // Pin savings-tracker scope to whichever dashboard mode the user is on
    // (approve / authorise / held) so the numbers ALWAYS match the queue
    // they're looking at. See /api/savings — `mode` selects Auth_by vs App_by
    // and reuses fetch_prs' queue-gating so parity is guaranteed.
    if (mode) params.mode = mode;
    return params;
  };

  const load = async (m) => {
    setLoading(true);
    try {
      const { data } = await prsApi.savings(buildParams(m));
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const loadAll = async () => {
    setAllLoading(true);
    try {
      const { data } = await prsApi.savings(buildParams(month, { limit: 1000 }));
      setAllRows(data.top || []);
    } catch {
      setAllRows([]);
    } finally {
      setAllLoading(false);
    }
  };

  useEffect(() => { load(month); }, [month, approver, mode]);

  return (
    <div
      data-testid="savings-banner"
      className="relative overflow-hidden rounded-sm bg-gradient-to-r from-emerald-900 via-emerald-800 to-teal-900 text-white p-4 sm:p-5 shadow-md"
    >
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: "radial-gradient(circle at 80% 50%, #ffffff 1px, transparent 1px)",
        backgroundSize: "20px 20px",
      }} />
      <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-lg bg-white/15 flex items-center justify-center">
            <Sparkles size={22} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-200 font-semibold">
              Savings Tracker · {data?.period || month}
            </div>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-6 gap-y-1">
              <div className="flex items-baseline gap-2 flex-wrap">
                <TrendingDown size={16} className="text-emerald-300" />
                <span className="text-2xl sm:text-3xl font-extrabold num">
                  ₹{loading ? "…" : fmtAmt(data?.potential)}
                </span>
                <span className="text-[10px] sm:text-xs text-emerald-200 uppercase tracking-wider">
                  Potential ({data?.count_potential || 0} open PRs)
                </span>
              </div>
              <div className="flex items-baseline gap-2 flex-wrap">
                <XCircle size={16} className="text-rose-300" />
                <span className="text-lg sm:text-xl font-bold num text-rose-100">
                  ₹{loading ? "…" : fmtAmt(data?.missed)}
                </span>
                <span className="text-[10px] sm:text-xs text-rose-200 uppercase tracking-wider">
                  Missed ({data?.count_missed || 0} approved PRs)
                </span>
              </div>
              <div className="flex items-baseline gap-2 flex-wrap">
                <AlertTriangle size={16} className="text-yellow-300" />
                <span className="text-lg sm:text-xl font-bold num text-emerald-100">
                  ₹{loading ? "…" : fmtAmt(data?.realized)}
                </span>
                <span className="text-[10px] sm:text-xs text-emerald-200 uppercase tracking-wider">
                  Realized ({data?.count_realized || 0} rejected PRs)
                </span>
              </div>
            </div>
            <p className="text-[10px] sm:text-xs text-emerald-200 mt-1 max-w-2xl hidden sm:block">
              <b>Potential</b> = saving still capturable on <b>Open / Hold</b> PRs.{" "}
              <b>Missed</b> = saving already lost on <b>Approved</b> PRs (closed, cannot be acted on).{" "}
              <b>Realized</b> = saving captured by <b>Rejecting</b> &amp; re-sourcing.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="savings-month"
            className="bg-white/10 border border-white/20 text-white text-sm rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-300"
          />
          {data?.top?.length > 0 && (
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="savings-breakdown-btn"
                  className="border-white/30 text-white hover:bg-white/10 hover:text-white bg-transparent"
                >
                  Top {data.top.length}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Top savings opportunities — {data.period}</DialogTitle>
                  <DialogDescription>
                    Top {data.top.length} of {(data.count_potential || 0) + (data.count_realized || 0) + (data.count_missed || 0)} PRs.
                    Click a row to open it.
                  </DialogDescription>
                </DialogHeader>
                <div className="max-h-[400px] overflow-y-auto scroll-area">
                  <SavingsList rows={data.top} onPrClick={onPrClick} />
                </div>
              </DialogContent>
            </Dialog>
          )}
          {((data?.count_potential || 0) + (data?.count_realized || 0) + (data?.count_missed || 0)) > (data?.top?.length || 0) && (
            <Dialog onOpenChange={(open) => { if (open && !allRows) loadAll(); }}>
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  data-testid="savings-all-btn"
                  className="bg-white text-emerald-900 hover:bg-emerald-50 font-semibold"
                >
                  <ArrowDownWideNarrow size={14} className="mr-1" />
                  All by Savings
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl">
                <DialogHeader>
                  <DialogTitle>All PRs ranked by savings — {data?.period}</DialogTitle>
                  <DialogDescription>
                    {(data?.count_potential || 0) + (data?.count_realized || 0) + (data?.count_missed || 0)} PRs with cheaper-vendor opportunities, descending by ₹ savings.
                    Click a row to open that PR.
                  </DialogDescription>
                </DialogHeader>
                <div className="max-h-[60vh] overflow-y-auto scroll-area">
                  {allLoading ? (
                    <div className="flex items-center justify-center py-12 text-slate-500 text-sm">
                      <Loader2 className="animate-spin mr-2" size={16} />
                      Loading all PRs...
                    </div>
                  ) : (
                    <SavingsList rows={allRows || []} onPrClick={onPrClick} dense />
                  )}
                </div>
              </DialogContent>
            </Dialog>
          )}
          {loading && <Loader2 className="animate-spin text-white/80" size={16} />}
        </div>
      </div>
    </div>
  );
}

function SavingsList({ rows, onPrClick, dense }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="text-center py-10 text-sm text-slate-500">
        No savings opportunities for this period.
      </div>
    );
  }
  const py = dense ? "py-1.5" : "py-2";
  return (
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider sticky top-0">
        <tr>
          <th className="text-left px-3 py-2 w-8">#</th>
          <th className="text-left px-3 py-2">PR No</th>
          <th className="text-left px-3 py-2">Supplier</th>
          <th className="text-left px-3 py-2 hidden sm:table-cell">Status</th>
          <th className="text-left px-3 py-2">Kind</th>
          <th className="text-right px-3 py-2">Savings</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => {
          const clickable = typeof onPrClick === "function";
          return (
            <tr
              key={row.voc_no}
              data-testid={`savings-row-${row.voc_no}`}
              onClick={clickable ? () => onPrClick(row.voc_no) : undefined}
              className={`border-t border-slate-100 ${clickable ? "cursor-pointer hover:bg-cyan-50" : ""}`}
            >
              <td className={`px-3 ${py} text-xs text-slate-400 num`}>{i + 1}</td>
              <td className={`px-3 ${py} mono font-semibold`}>{row.voc_no}</td>
              <td className={`px-3 ${py} max-w-[220px] truncate`} title={row.supplier}>{row.supplier}</td>
              <td className={`px-3 ${py} text-slate-700 hidden sm:table-cell`}>{row.status || "Open"}</td>
              <td className={`px-3 ${py}`}>
                <span className={`inline-flex text-[10px] px-2 py-0.5 rounded-full font-semibold border ${
                  row.kind === "realized"
                    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                    : row.kind === "missed"
                    ? "bg-rose-50 text-rose-800 border-rose-200"
                    : "bg-yellow-50 text-yellow-900 border-yellow-200"
                }`}>
                  {row.kind}
                </span>
              </td>
              <td className={`px-3 ${py} text-right num font-semibold`}>₹{fmtAmt(row.savings)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

